import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

const ButtonGroupContext = React.createContext({})

/**
 * Bronson ButtonGroup component.
 * @param children
 * @param {string} [className] - Additional class names for the container.
 * @param {string} [componentDescription] - A component description. Should not be used in production.
 * @param {string} [errorMessage] - A string to display validation errors.
 * @param {string} [buttonGroupType] - The type of button group behavior.
 *                                   Can be either 'radio' or 'checkbox'.
 * @param {string} [buttonGroupName] - The value for grouping the {@link ButtonGroupItem} via their `name` attribute.
 * @param {boolean} [auto] - Flag to switch on auto-sizing of the {@link ButtonGroupItem}.
 *                         Prevents stretching the items over the whole width of the container.
 * @return {JSX.Element} - The ButtonGroup element.
 * @constructor
 */
export function ButtonGroup({
  componentDescription,
  className,
  errorMessage,
  buttonGroupType,
  buttonGroupName,
  children,
  auto,
  // ...otherProps /* in no tag */
}) {
  const contextValue = {
    buttonGroupType,
    buttonGroupName,
  }

  // generated
  function renderIfComponentDescription() {
    if (componentDescription) {
      return <p>{componentDescription}</p>
    }
    return null
  }

  // generated
  const divClassNameList = classNames(
    {
      'c-button-group ': true,
      // main class modifier convenience prop extension
      'c-button-group--auto ': auto, // Convenience prop from Bronson variants.
    },
    className
  ).trim()

  // generated
  function renderIfButtonGroupErrorMessage() {
    if (errorMessage) {
      return <p className="c-error-message">{errorMessage}</p>
    }
    return null
  }

  // generated main result
  return (
    <>
      {renderIfComponentDescription()}
      <div className={divClassNameList}>
        <ButtonGroupContext.Provider value={contextValue}>
          {children} {/* Use 'ButtonGroup.Item' component. */}
        </ButtonGroupContext.Provider>
      </div>
      {renderIfButtonGroupErrorMessage()}
    </>
  )
}

ButtonGroup.propTypes = {
  componentDescription: PropTypes.string, // Bronson template: 'component-description'.
  className: PropTypes.string, // Bronson template: 'button-group-modifier'.
  errorMessage: PropTypes.node, // Bronson template: 'button-group-error-message'.
  buttonGroupType: PropTypes.oneOf(['radio', 'checkbox']), // Bronson template: 'button-group-type'.
  children: PropTypes.node, // Bronson template: 'button-group-items'. Use 'ButtonGroup.Item' component.
  /* Convenience props */
  auto: PropTypes.bool, // Convenience prop for c-button-group--auto (Bronson template: 'button-group-modifier').
  buttonGroupName: PropTypes.string, //  Convenience prop to set 'handle' prop for all ButtonGroup.Items
}

/**
 * Bronson ButtonGroupItem component (nested).
 * @param children
 * @param {string} [handle] - The 'name' attribute value. If not set directly the {@link context.buttonGroupName} is used.
 * @param {number} [index] - The item’s current index.
 * @param {string} [buttonGroupItemIcon] - The icon to use for the item, if any.
 * @param {string} [testId] - The testId string on the item.
 * @param {object} [otherProps] - Other properties to pass to the ButtonGroupItem element.
 * @return {JSX.Element} - The ButtonGroupItem element.
 * @constructor
 */
function ButtonGroupItem({ handle, index, buttonGroupItemIcon, children, testId, ...otherProps /* in <label> tag */ }) {
  const context = useContext(ButtonGroupContext)
  // eslint-disable-next-line no-param-reassign
  handle = handle || context.buttonGroupName

  // generated
  const iClassNameList = classNames({
    'c-button-group__icon ': true,
    'c-icon ': true,
    [`c-icon--[${buttonGroupItemIcon}] `]: true,
  }).trim()

  // generated
  function renderIfButtonGroupItemIcon() {
    if (buttonGroupItemIcon) {
      return <i className={iClassNameList} aria-hidden="true" role="img" />
    }
    return null
  }

  // generated main result
  return (
    <label data-testid={testId} className="c-button-group__item" htmlFor={`button-group-item-${handle}-${index}`}>
      <input
        className="c-button-group__input"
        type={context.buttonGroupType}
        name={`button-group-item-${handle}`}
        id={`button-group-item-${handle}-${index}`}
        {...otherProps}
      />
      <span className="c-button-group__label">
        {renderIfButtonGroupItemIcon()}
        {children}
      </span>
    </label>
  )
}

ButtonGroupItem.propTypes = {
  handle: PropTypes.string, // Bronson template: 'handle'. Use a unique name for the button group
  index: PropTypes.string, // Bronson template: 'index'.
  buttonGroupItemIcon: PropTypes.string, // Bronson template: 'button-group-item-icon'.
  children: PropTypes.node, // Bronson template: 'button-group-item-label'.
  testId: PropTypes.string, // Added for data-testid attribute.
  // Bronson template: 'button-group-item-attribute' -> just pass input props directly, e.g. "checked"
}

ButtonGroupItem.displayName = 'ButtonGroup.Item'
ButtonGroup.Item = ButtonGroupItem
